import type { LucideIcon } from "lucide-react";
import {
  BarChart2,
  Building2,
  ClipboardList,
  Crown,
  FileSpreadsheet,
  FolderTree,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  Megaphone,
  MessageCircle,
  Package,
  Scale,
  Settings,
  Shield,
  ShoppingCart,
  Tag,
  Ticket,
  Truck,
  UserCog,
  Users,
  Warehouse,
} from "lucide-react";
import type { UserRole } from "@/lib/types/user";

export type NavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
};

export type NavGroup = {
  id: string;
  label: string;
  icon: LucideIcon;
  basePath: string;
  children: NavItem[];
};

type NavDef = NavItem & { perm?: string };

const MARKETING_CHILDREN: NavItem[] = [
  { label: "WhatsApp", href: "/marketing/whatsapp", icon: MessageCircle },
  { label: "Vouchers", href: "/marketing/vouchers", icon: Ticket },
  { label: "Offers", href: "/marketing/offers", icon: Tag },
  { label: "Catalogue", href: "/marketing/catalogue", icon: LayoutGrid },
];

const REPORTS_CHILDREN: NavItem[] = [
  { label: "Sales register", href: "/reports/sales", icon: FileSpreadsheet },
  { label: "GSTR-1", href: "/reports/gstr1", icon: Scale },
  { label: "GSTR-3B", href: "/reports/gstr3b", icon: Scale },
  { label: "Stock & expiry", href: "/reports/stock", icon: Package },
  { label: "P&L", href: "/reports/profit", icon: LineChart },
];

/** Main nav: optional `perm` = require module.action from /api/auth/me */
const NAV_DEFS: NavDef[] = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard, perm: "reports.view" },
  { label: "POS Billing", href: "/pos", icon: ShoppingCart, perm: "sales.create" },
  { label: "Products", href: "/products", icon: Package, perm: "products.view" },
  { label: "Categories", href: "/products/categories", icon: FolderTree, perm: "products.view" },
  { label: "Suppliers", href: "/suppliers", icon: Warehouse, perm: "purchase.view" },
  { label: "Purchase", href: "/purchase/orders", icon: Truck, perm: "purchase.view" },
  { label: "Customers", href: "/customers", icon: Users, perm: "customers.view" },
  { label: "Settings", href: "/settings", icon: Settings, perm: "settings.view" },
];

const CASHIER_HREFS = new Set(["/pos", "/products", "/products/categories", "/reports"]);

type Me = {
  role: UserRole;
  permissions?: string[];
};

export type NavFlags = {
  /** When false, hide Admin · Branches. Default true (until settings say otherwise). */
  branchesEnabled?: boolean;
};

export function navItemsForUser(user: Me | null | undefined, flags?: NavFlags): NavItem[] {
  const perms = user?.permissions ?? [];
  const role = user?.role;

  if (role === "product_admin") {
    return [
      { label: "Dashboard", href: "/product-admin/dashboard", icon: LayoutDashboard },
      { label: "Companies", href: "/product-admin/companies", icon: Crown },
      { label: "Leads", href: "/product-admin/leads", icon: Megaphone },
      { label: "Attendance", href: "/product-admin/attendance", icon: ClipboardList },
      { label: "Team", href: "/product-admin/users", icon: UserCog },
    ];
  }

  if (role === "product_admin_staff") {
    return [
      { label: "Dashboard", href: "/product-admin/dashboard", icon: LayoutDashboard },
      { label: "Leads", href: "/product-admin/leads", icon: Megaphone },
    ];
  }

  let list: NavItem[] = NAV_DEFS.filter((i) => !i.perm || perms.includes(i.perm)).map(
    ({ perm: _p, ...rest }) => rest,
  );

  if (role === "cashier") {
    list = list.filter((i) => CASHIER_HREFS.has(i.href));
  }

  const adminNav: NavItem[] = [];

  if (role === "admin") {
    adminNav.push(
      { label: "Admin · Users", href: "/admin/users", icon: Users },
      { label: "Admin · Roles", href: "/admin/roles", icon: Shield },
      { label: "Admin · Audit", href: "/admin/audit", icon: ClipboardList },
      { label: "Admin · Shifts", href: "/admin/shifts", icon: ClipboardList },
    );
    if (flags?.branchesEnabled !== false) {
      adminNav.push({ label: "Admin · Branches", href: "/admin/branches", icon: Building2 });
    }
  }

  return [...list, ...adminNav];
}

export function navGroupsForUser(user: Me | null | undefined): NavGroup[] {
  const role = user?.role;
  const perms = user?.permissions ?? [];

  if (role === "product_admin") {
    return [];
  }

  const groups: NavGroup[] = [];

  if (role === "product_admin_staff") {
    groups.push({
      id: "marketing",
      label: "Marketing",
      icon: Megaphone,
      basePath: "/marketing",
      children: MARKETING_CHILDREN,
    });

    return groups;
  }

  if (role !== "cashier") {
    groups.push({
      id: "marketing",
      label: "Marketing",
      icon: Megaphone,
      basePath: "/marketing",
      children: MARKETING_CHILDREN,
    });
  }

  if (perms.includes("reports.view") || role === "cashier") {
    const children =
      role === "cashier"
        ? REPORTS_CHILDREN.filter((c) => c.href === "/reports/sales")
        : REPORTS_CHILDREN;

    groups.push({
      id: "reports",
      label: "Reports",
      icon: BarChart2,
      basePath: "/reports",
      children,
    });
  }

  return groups;
}
